import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, bookingApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modals state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getBookings({ limit: 100 });
      const raw = res.bookings || res.data || [];
      
      const mapped = raw.map(b => {
        const client = b.client || b.clientId || {};
        const physioProfile = b.physiotherapistProfileId || {};
        const physioUser = physioProfile.userId || b.therapist || {};

        const resolvedClientName = client.name || b.clientName || 'Client';
        const resolvedClientEmail = client.email || b.clientEmail || 'N/A';
        const resolvedClientPhone = client.phone || b.clientPhone || 'N/A';

        const resolvedTherapistName = physioUser.name || physioProfile.name || b.therapistName || 'Therapist';
        const resolvedTherapistEmail = physioUser.email || physioProfile.email || b.therapistEmail || 'N/A';
        const resolvedTherapistPhone = physioUser.phone || physioProfile.phone || b.therapistPhone || 'N/A';
        const resolvedSpec = physioUser.specialization || physioProfile.specialization || b.specialization || 'Clinical Psychologist';
        const resolvedLoc = physioUser.location || physioProfile.location || b.location || 'Not Specified';
        const resolvedExp = physioUser.yearsOfExperience || physioProfile.yearsOfExperience || b.yearsOfExperience || 0;
        const resolvedBio = physioUser.bio || physioProfile.bio || '';

        const scheduledDateObj = b.scheduledAt ? new Date(b.scheduledAt) : null;
        const dateStr = scheduledDateObj ? scheduledDateObj.toISOString().split('T')[0] : (b.date || 'N/A');
        const timeStr = scheduledDateObj ? scheduledDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (b.time || '10:00 AM');

        return {
          _id: b._id || b.id,
          id: b._id || b.id,
          rawBooking: b,
          // Client info
          clientName: resolvedClientName,
          clientEmail: resolvedClientEmail,
          clientPhone: resolvedClientPhone,
          // Therapist info
          therapistName: resolvedTherapistName,
          therapistEmail: resolvedTherapistEmail,
          therapistPhone: resolvedTherapistPhone,
          therapistSpec: resolvedSpec,
          therapistLoc: resolvedLoc,
          therapistExp: resolvedExp,
          therapistBio: resolvedBio,
          // Session details
          scheduledAt: b.scheduledAt,
          date: dateStr,
          time: timeStr,
          duration: String(b.durationMinutes || b.duration || 60),
          mode: b.mode || b.sessionType || 'online',
          status: b.status || 'pending',
          notes: b.notes || 'Standard consultation',
          googleMeetLink: b.googleMeetLink || b.meetingLink || 'https://meet.google.com/new',
          offlineAddress: b.offlineAddress || '',
          calendarEventUrl: b.calendarEventUrl || '',
          bookingDate: b.createdAt ? new Date(b.createdAt).toISOString().split('T')[0] : 'N/A'
        };
      });

      setBookings(mapped);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchBookings();
  }, [authSession?.token, navigate]);

  const handleUpdateStatus = async (bookingId, newStatus) => {
    setIsSubmitting(true);
    try {
      await adminApi.updateBookingStatus(bookingId, { status: newStatus });
      setBookings(bookings.map(b => b._id === bookingId ? { ...b, status: newStatus } : b));
      if (editBooking && editBooking._id === bookingId) {
        setEditBooking({ ...editBooking, status: newStatus });
      }
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking({ ...selectedBooking, status: newStatus });
      }
      alert(`Booking #${bookingId.slice(-6)} status updated to ${newStatus.toUpperCase()}`);
      setEditBooking(null);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert(err.message || 'Failed to update status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = 
      b.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.therapistName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.therapistEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const paginatedBookings = filteredBookings.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed' || b.status === 'approved').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled' || b.status === 'rejected').length
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'confirmed':
      case 'approved':
        return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: '✅ Approved' };
      case 'pending':
        return { bg: '#fef3c7', color: '#92400e', border: '#fde68a', label: '⏳ Pending' };
      case 'completed':
        return { bg: '#e0f2fe', color: '#075985', border: '#bae6fd', label: '🎉 Completed' };
      case 'cancelled':
      case 'rejected':
        return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', label: '❌ Rejected' };
      default:
        return { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb', label: status };
    }
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ marginLeft: '280px', padding: '30px', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h1 style={{ color: '#111827', margin: '0 0 5px 0', fontSize: '28px', fontWeight: 800 }}>
              📅 Admin Booking Management
            </h1>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
              View client & therapist booking details, approve or reject session applications
            </p>
          </div>

          <button
            onClick={fetchBookings}
            style={{
              padding: '10px 18px',
              backgroundColor: '#4f46e5',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '14px',
              boxShadow: '0 2px 5px rgba(79,70,229,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔄 Refresh List
          </button>
        </div>

        {error && (
          <div style={{ padding: '14px 18px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '10px', marginBottom: '20px', fontWeight: 600 }}>
            ⚠️ {error}
          </div>
        )}

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '30px' }}>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', fontWeight: 600 }}>📋 TOTAL BOOKINGS</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>{stats.total}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: '12px', color: '#166534', marginBottom: '6px', fontWeight: 600 }}>✅ APPROVED / CONFIRMED</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#166534' }}>{stats.confirmed}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #fde68a' }}>
            <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '6px', fontWeight: 600 }}>⏳ PENDING REVIEW</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#92400e' }}>{stats.pending}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #fca5a5' }}>
            <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '6px', fontWeight: 600 }}>❌ REJECTED / CANCELLED</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#991b1b' }}>{stats.cancelled}</div>
          </div>
        </div>

        {/* Filter Controls */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="🔍 Search client or therapist name/email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', flex: '1', minWidth: '250px' }}
          />

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: '#fff', fontWeight: 600 }}
          >
            <option value="all">Filter by Status (All)</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Approved / Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Rejected / Cancelled</option>
          </select>
        </div>

        {/* Bookings Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>Loading bookings & user profiles...</p>
          ) : filteredBookings.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Client Details</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Therapist Details</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Session Time</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Mode</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '700', color: '#374151', fontSize: '13px', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBookings.map((b) => {
                    const badge = getStatusBadgeStyle(b.status);
                    return (
                      <tr key={b._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        {/* Client Details */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#111827', fontSize: '14px' }}>👤 {b.clientName}</div>
                          <div style={{ fontSize: '12px', color: '#4b5563' }}>📧 {b.clientEmail}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>📞 {b.clientPhone}</div>
                        </td>

                        {/* Therapist Details */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#111827', fontSize: '14px' }}>👨‍⚕️ {b.therapistName}</div>
                          <div style={{ fontSize: '12px', color: '#4b5563' }}>🩺 {b.therapistSpec}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>📧 {b.therapistEmail}</div>
                        </td>

                        {/* Session Time */}
                        <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>
                          <div style={{ fontWeight: 700 }}>📅 {b.date}</div>
                          <div style={{ color: '#6b7280' }}>🕐 {b.time} ({b.duration} min)</div>
                        </td>

                        {/* Mode */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '16px',
                            backgroundColor: b.mode === 'online' ? '#eff6ff' : '#fdf2f8',
                            color: b.mode === 'online' ? '#1d4ed8' : '#be185d',
                            fontSize: '12px',
                            fontWeight: 700,
                            border: b.mode === 'online' ? '1px solid #bfdbfe' : '1px solid #fbcfe8'
                          }}>
                            {b.mode === 'online' ? '💻 Online' : '🏥 In-Person'}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '20px',
                            backgroundColor: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            fontSize: '12px',
                            fontWeight: 700,
                            display: 'inline-block'
                          }}>
                            {badge.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => setSelectedBooking(b)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              👁️ View Details
                            </button>

                            <button
                              onClick={() => setEditBooking(b)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#8b5cf6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Update Status
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>No bookings found matching filter</p>
          )}

          {!loading && filteredBookings.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredBookings.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newSize) => {
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            />
          )}
        </div>

        {/* VIEW BOOKING DETAILS MODAL */}
        {selectedBooking && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb' }}>
              <div style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', padding: '20px 25px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>📄 Full Session & User Details</h2>
                  <span style={{ fontSize: '12px', opacity: 0.9 }}>Booking ID: #{selectedBooking._id}</span>
                </div>
                <button onClick={() => setSelectedBooking(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: '18px', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' }}>✕</button>
              </div>

              <div style={{ padding: '24px' }}>
                {/* Client Info */}
                <div style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>👤 Client Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div><strong>Name:</strong> {selectedBooking.clientName}</div>
                    <div><strong>Email:</strong> {selectedBooking.clientEmail}</div>
                    <div><strong>Phone:</strong> {selectedBooking.clientPhone}</div>
                  </div>
                </div>

                {/* Therapist Info */}
                <div style={{ marginBottom: '20px', padding: '16px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>👨‍⚕️ Assigned Therapist Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px', color: '#14532d' }}>
                    <div><strong>Name:</strong> {selectedBooking.therapistName}</div>
                    <div><strong>Email:</strong> {selectedBooking.therapistEmail}</div>
                    <div><strong>Phone:</strong> {selectedBooking.therapistPhone}</div>
                    <div><strong>Specialization:</strong> {selectedBooking.therapistSpec}</div>
                    <div><strong>Location:</strong> {selectedBooking.therapistLoc}</div>
                    <div><strong>Experience:</strong> {selectedBooking.therapistExp} years</div>
                  </div>
                  {selectedBooking.therapistBio && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#15803d' }}>
                      <strong>Bio:</strong> {selectedBooking.therapistBio}
                    </div>
                  )}
                </div>

                {/* Session Details */}
                <div style={{ marginBottom: '20px', padding: '16px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>⏰ Session & Meeting Info</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px', color: '#0c4a6e', marginBottom: '10px' }}>
                    <div><strong>Scheduled Date:</strong> {selectedBooking.date}</div>
                    <div><strong>Time:</strong> {selectedBooking.time}</div>
                    <div><strong>Duration:</strong> {selectedBooking.duration} mins</div>
                    <div><strong>Session Mode:</strong> {selectedBooking.mode === 'online' ? '💻 Online' : '🏥 In-Person'}</div>
                  </div>

                  {selectedBooking.mode === 'online' && (
                    <div style={{ marginTop: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#1d4ed8', display: 'block', marginBottom: '4px' }}>🎥 Google Meet Room URL:</span>
                      <a href={selectedBooking.googleMeetLink} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 700, fontSize: '13px', wordBreak: 'break-all' }}>
                        {selectedBooking.googleMeetLink}
                      </a>
                    </div>
                  )}

                  {selectedBooking.mode === 'offline' && selectedBooking.offlineAddress && (
                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#0369a1' }}>
                      <strong>Clinic Address:</strong> {selectedBooking.offlineAddress}
                    </div>
                  )}

                  {selectedBooking.notes && (
                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#475569' }}>
                      <strong>Notes:</strong> {selectedBooking.notes}
                    </div>
                  )}
                </div>

                {/* Actions inside view modal */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    onClick={() => { setSelectedBooking(null); setEditBooking(selectedBooking); }}
                    style={{ padding: '10px 20px', backgroundColor: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✏️ Change Status
                  </button>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    style={{ padding: '10px 20px', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EDIT / CHANGE STATUS MODAL */}
        {editBooking && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '500px', width: '100%', padding: '24px', boxShadow: '0 20px 25px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#111827' }}>
                  ⚡ Change Booking Status
                </h3>
                <button onClick={() => setEditBooking(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>

              <div style={{ marginBottom: '16px', fontSize: '14px', color: '#374151' }}>
                <div><strong>Client:</strong> {editBooking.clientName} ({editBooking.clientEmail})</div>
                <div><strong>Therapist:</strong> {editBooking.therapistName}</div>
                <div><strong>Current Status:</strong> <span style={{ fontWeight: 700 }}>{editBooking.status}</span></div>
              </div>

              {/* Quick Approve / Reject Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <button
                  disabled={isSubmitting}
                  onClick={() => handleUpdateStatus(editBooking._id, 'confirmed')}
                  style={{
                    padding: '12px',
                    backgroundColor: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 3px 8px rgba(16,185,129,0.3)'
                  }}
                >
                  ✓ Approve Booking
                </button>

                <button
                  disabled={isSubmitting}
                  onClick={() => handleUpdateStatus(editBooking._id, 'cancelled')}
                  style={{
                    padding: '12px',
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 3px 8px rgba(239,68,68,0.3)'
                  }}
                >
                  ✗ Reject Booking
                </button>
              </div>

              {/* Select Other Status */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  Or Select Custom Status:
                </label>
                <select
                  value={editBooking.status}
                  onChange={(e) => setEditBooking({ ...editBooking, status: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                >
                  <option value="confirmed">Confirmed / Approved</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled / Rejected</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  disabled={isSubmitting}
                  onClick={() => handleUpdateStatus(editBooking._id, editBooking.status)}
                  style={{ padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Save Status
                </button>
                <button
                  onClick={() => setEditBooking(null)}
                  style={{ padding: '10px 20px', backgroundColor: '#9ca3af', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminBookingsPage;
